/* istanbul ignore file */
/**
 * Created by rathompson on 11/3/21
 * File: CustomRoleForm
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useMemo, useState, useEffect, VFC, Dispatch, SetStateAction } from 'react'
import {
  Title,
  Button,
  Bold,
} from '@panwds/react-ui'
import {
  Form,
  Input,
  Textarea as PanTextArea,
  SubmitButton
} from '@panwds/react-form'
import {
  LABEL_ADD_CUSTOM_ROLE_TITLE,
  BUTTON_SAVE,
  BUTTON_CANCEL,
  LABEL_ROLE_DESC,
  LABEL_ROLE_NAME,
  PLACEHOLDER_ROLE_NAME,
  PLACEHOLDER_ROLE_DESC,
  LABEL_EDIT_CUSTOM_ROLE_TITLE,
  ERROR_ROLE_NAME_INVALID,
  ERROR_ROLE_DESC_INVALID,
  ERROR_ROLE_EXISTS,
  LABEL_DISPLAY_NAME_OPTIONAL,
  PLACEHOLDER_ROLE_DISPLAY_NAME,
  ERROR_ROLE_LABEL_INVALID,
  ERROR_PERMISSIONS_REQUIRED,
} from '../../constants/LabelConstants'
import AddPermissionsTable from './table/addPermissionsTable/AddPermissionsTable'
import { setCurrentViewing } from '../../../common/utils/Sparky'
import { VIEW_IAM_ROLES } from '../../../common/constants/GlobalConstants'
import { useFormContext } from 'react-hook-form'
import ScreenLoader from '../../../common/components/screenLoader/ScreenLoader'
import BreadCrumbs from '../../../common/components/breadcrumbs/BreadCrumbs'
import GlobalContext from '../../../common/context/GlobalContext'
import { OBJECT } from '../../../common/utils/Utils'
import { CUSTOM_ROLE_FORM_ACTIONS } from '../../constants/IAMConstants'
import IAMService from '../../services/IAMService'
import { useMessage } from '../../../common/providers/MessageProvider'
import { useLoadingContext } from '../../../common/context/LoadingContext'
import StringTemplate from '../../../common/utils/StringTemplate'
import type { IAM_Permission, IAM_PermissionSet, IAM_Role } from '../../../common/reducers/types/IAMReducer.types'
import type { I_LeafPermission } from '../../utils/buildPermissionTree'
import _ from 'lodash'
import styled from 'styled-components'

const TextArea = PanTextArea as any // TODO: remove any, only here as temp fix for "placeholder" and "showLabel" props

const CONTAINER = styled.div`
  @media only screen and (max-height: 1280px) {
    overflow: auto;
  }
`

const FORM_WRAPPER = styled.div`
  @media only screen and (max-height: 1280px) {
    height: min-content;
  }
`

const Footer: VFC<{
  onCancel: () => void
}> = ({ onCancel }) => {
  return (
    <div className='cm-p-3 cm-flex cm-gap-2 cm-justify-end' style={{ height: '50px' }}>
      <Button appearance='secondary' onClick={onCancel}>{BUTTON_CANCEL}</Button>
      <SubmitButton>{BUTTON_SAVE}</SubmitButton>
    </div>
  )
}

const FormLayout: VFC<{
  role: IAM_Role | undefined,
  editRole: IAM_Role | undefined,
  setRole: Dispatch<SetStateAction<IAM_Role | undefined>>,
  onCancel: () => void,
  setWebData: Dispatch<SetStateAction<IAM_PermissionSet[]>>,
  setAPIData: Dispatch<SetStateAction<IAM_Permission[]>>
}> = ({ role, editRole, setRole, onCancel, setWebData, setAPIData }) => {

  const { iamReducer } = useContext(GlobalContext)
  const { roles, customRoles, customRoleFormMetaData } = iamReducer
  const { getValues, setValue, trigger } = useFormContext()

  const isEdit = useMemo(() => {
    const { action } = customRoleFormMetaData
    return Boolean(action === CUSTOM_ROLE_FORM_ACTIONS.EDIT && OBJECT.hasObject(role))
  }, [customRoleFormMetaData, role])

  useEffect(() => {
    if (OBJECT.hasObject(role)) {
      const { name, description, label } = role || {}
      const { action } = customRoleFormMetaData
      const nameInput = getValues('name')
      if (action !== CUSTOM_ROLE_FORM_ACTIONS.EDIT || (!nameInput && action === CUSTOM_ROLE_FORM_ACTIONS.EDIT)) {
        setValue('name', name, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
      }
      setValue('description', description, { shouldDirty: true, shouldTouch: true, shouldValidate: true })
      setValue('label', label || '', { shouldDirty: true, shouldTouch: true, shouldValidate: true })
      trigger()
    }
  }, [getValues, role, setValue, trigger, customRoleFormMetaData])

  const roleExists = useCallback((value) => {
    const { action } = customRoleFormMetaData
    if (action === CUSTOM_ROLE_FORM_ACTIONS.EDIT && editRole?.name === value) {
      return true
    }
    return ([...roles, ...customRoles]?.find(role => role.name === value || role.label === value) ? StringTemplate(ERROR_ROLE_EXISTS, [value]) as string : true)
  }, [customRoleFormMetaData, customRoles, editRole?.name, roles])

  const labelExists = useCallback((value) => {
    const { action } = customRoleFormMetaData
    if (action === CUSTOM_ROLE_FORM_ACTIONS.EDIT && role?.label === editRole?.label && role?.label === value) {
      return true
    }
    return ([...roles, ...customRoles]?.find(role => role.label === value) ? StringTemplate(ERROR_ROLE_EXISTS, [value]) as string : true)
  }, [customRoleFormMetaData, customRoles, editRole, role?.label, roles])

  return (
    <FORM_WRAPPER className="cm-absolute cm-inset-0 cm-flex cm-flex-col">
      <div className='cm-w-1/2'>
        <div>
          <Bold appearance='semibold'>{LABEL_ROLE_NAME}</Bold>
          <Input
            showLabel={false}
            name={'name'}
            label={LABEL_ROLE_NAME}
            placeholder={PLACEHOLDER_ROLE_NAME}
            disabled={isEdit}
            register={{
              maxLength: { value: 20, message: ERROR_ROLE_NAME_INVALID },
              pattern: { value: /^[a-zA-Z0-9_]*$/gm, message: ERROR_ROLE_NAME_INVALID },
              setValueAs: value => value.trim(),
              onChange: async () => trigger('name'),
              validate: {
                alreadyExists: roleExists
              }
            }}
          />
        </div>
        <div>
          <Bold appearance='semibold'>{LABEL_DISPLAY_NAME_OPTIONAL}</Bold>
          <Input
            showLabel={false}
            name={'label'}
            label={LABEL_DISPLAY_NAME_OPTIONAL}
            placeholder={PLACEHOLDER_ROLE_DISPLAY_NAME}
            required={false}
            register={{
              required: false,
              maxLength: { value: 256, message: ERROR_ROLE_LABEL_INVALID },
              setValueAs: value => value.trim(),
              onChange: async () => trigger('label'),
              validate: {
                alreadyExists: labelExists
              }
            }}
          />
        </div>
        <div>
          <Bold appearance='semibold'>{LABEL_ROLE_DESC}</Bold>
          <TextArea
            showLabel={false}
            name={'description'}
            label={LABEL_ROLE_DESC}
            placeholder={PLACEHOLDER_ROLE_DESC}
            rowsMin={3}
            rowsMax={10}
            register={{
              maxLength: { value: 512, message: ERROR_ROLE_DESC_INVALID },
              setValueAs: (value: string) => value.trim(),
              onChange: async () => trigger('description')
            }}
          />
        </div>
      </div>
      <div className='cm-flex cm-flex-1 cm-flex-col cm-overflow-hidden cm-pt-2' style={{ maxHeight: 'calc(100% - 50px)' }}>
        <AddPermissionsTable { ...{ role, setRole, setWebData, setAPIData } } expanded={false} />
        <Footer {...{ onCancel }} />
      </div>
    </FORM_WRAPPER>
  )
}

/**
 *   Custom roles form
 */
const CustomRoleForm: VFC<{
  permissions: IAM_Permission[],
  getData: Function
}> = ({ permissions: _permissions = [], getData }) => {

  const { iamReducer, dispatch, treeReducer } = useContext(GlobalContext)
  const { selectedTreeNode } = treeReducer
  const { customRoleFormMetaData, setMap } = iamReducer
  const { createBanner, clearBanner } = useMessage()

  const [webData, setWebData] = useState<IAM_PermissionSet[]>([])
  const [apiData, setAPIData] = useState<IAM_Permission[]>([])

  const breadcrumbs = useMemo(() => {
    const breadcrumbs = new Map()
    breadcrumbs.set('add_custom', { display_name: 'Add Custom Role', id: 'add_custom' })
    breadcrumbs.set('roles', { display_name: 'Roles', id: 'roles' })
    return breadcrumbs
  }, [])

  const { loading, setLoading } = useLoadingContext()
  const [role, setRole] = useState<IAM_Role | undefined>(undefined)
  const [editRole, setEditRole] = useState<IAM_Role | undefined>(undefined)

  useEffect(() => {
    if (OBJECT.hasObject(customRoleFormMetaData)) {
      const { role, action } = customRoleFormMetaData
      if (OBJECT.hasObject(role)) {
        setRole(role as IAM_Role)
        if (action === CUSTOM_ROLE_FORM_ACTIONS.EDIT) {
          setEditRole(role)
        }
      }
    }
  }, [customRoleFormMetaData, dispatch])

  const onCancel = useCallback(() => {
    setCurrentViewing(VIEW_IAM_ROLES)
  }, [])

  const onBreadCrumbClick = useCallback(() => {
    setCurrentViewing(VIEW_IAM_ROLES)
  }, [])

  const title = useMemo(() => {
    const { action } = customRoleFormMetaData
    switch (action) {
      case CUSTOM_ROLE_FORM_ACTIONS.ADD:
      case CUSTOM_ROLE_FORM_ACTIONS.CLONE:
        return LABEL_ADD_CUSTOM_ROLE_TITLE
      case CUSTOM_ROLE_FORM_ACTIONS.EDIT:
        return LABEL_EDIT_CUSTOM_ROLE_TITLE
      default:
        return ''
    }
  }, [customRoleFormMetaData])

  const handleCreate = useCallback(async (data) => {
    const {
      name,
      description,
      permissions,
      permission_sets,
      label
    } = data
    const params = OBJECT.reduce({
      name,
      description,
      permissions,
      permission_sets,
      label
    })
    await IAMService.customRoles.create(selectedTreeNode?.id, params)
  }, [selectedTreeNode?.id])

  const handleUpdate = useCallback(async (data) => {
    const { description, permissions, permission_sets, label } = data
    const { role } = customRoleFormMetaData
    await IAMService.customRoles.update(selectedTreeNode?.id, role?.name, OBJECT.reduce({ description, permissions, permission_sets, label }))
  }, [customRoleFormMetaData, selectedTreeNode?.id])

  const getPermissionSets = useCallback(() => {

    const tableData = webData.map(({ name, access_level }) => ({ id: name, access_types: [access_level] }))
    const tableMap: Record<string, any> = _.cloneDeep(tableData.reduce((map: any, item: any) => {
      map[item.id] = item
      return map
    }, {}))

    const permission_sets: Array<any> = _.cloneDeep(tableData)

    const updateChildren = (parent_set: any) => {
      const permission_set: IAM_PermissionSet = setMap[parent_set.id]
      _.get(permission_set, 'children', []).forEach((child_id: string) => {
        if (tableMap.hasOwnProperty(child_id)) {
          return
        }
        const child: IAM_PermissionSet = setMap[child_id]
        permission_sets.push({
          id: child.name,
          access_types: parent_set.access_types
        })
        updateChildren({ ...child, access_types: parent_set.access_types })
      })
    }

    for (const data_item of tableData) {
      updateChildren(data_item)
    }
    return permission_sets
  }, [webData, setMap])

  const onSubmit = useCallback(async (data) => {
    try {
      clearBanner()
      setLoading(true)
      const permissions = Array.from(new Set(apiData.filter((permission) => !permission.locked && (permission as I_LeafPermission).is_leaf).map((permission) => permission.name)))
      const permission_sets = getPermissionSets()
      if (_.isEmpty(permission_sets) && _.isEmpty(permissions)) {
        throw new Error(ERROR_PERMISSIONS_REQUIRED)
      }
      const { action } = customRoleFormMetaData
      switch (action) {
        case CUSTOM_ROLE_FORM_ACTIONS.EDIT:
          await handleUpdate({ permissions, permission_sets, ...data })
          break
        case CUSTOM_ROLE_FORM_ACTIONS.ADD:
        case CUSTOM_ROLE_FORM_ACTIONS.CLONE:
        default:
          await handleCreate({ permissions, permission_sets, ...data })
      }
      await getData(selectedTreeNode?.id, dispatch)
      onCancel()
    }
    catch (error: any) {
      createBanner({ message: error.message })
    }
    finally {
      setLoading(false)
    }
  }, [apiData, clearBanner, createBanner, customRoleFormMetaData, getPermissionSets, dispatch, getData, handleCreate, handleUpdate, onCancel, selectedTreeNode?.id, setLoading])

  return (
    <>
      <div className="cm-p-3" style={{ minHeight: '63px' }}>
        <BreadCrumbs style={{}} breadcrumbs={breadcrumbs} onClick={onBreadCrumbClick}/>
      </div>
      <div className='cm-pl-5 cm-pr-5 cm-pb-5'>
        <Title level={1} size='lg'>{title}</Title>
      </div>
      <div className='cm-overflow-hidden cm-flex-1 cm-flex cm-flex-col'>
        <div className='cm-flex cm-flex-col cm-h-full'>
          <div className='cm-flex cm-flex-col cm-h-full cm-border-t cm-border-solid cm-border-gray-200 dark:cm-border-gray-950 cm-border-l-0 cm-border-r-0 cm-border-b-0 cm-p-5'>
            {loading && (
              <ScreenLoader />
            )}
            <CONTAINER className='cm-h-full' style={{ minWidth: '720px' }}>
              <Form mode='onChange' onSubmit={onSubmit} formProps={{ style: { height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' } }}>
                <FormLayout { ...{ role, editRole, setRole, onCancel, setWebData, setAPIData } } />
              </Form>
            </CONTAINER>
          </div>
        </div>
      </div>
    </>
  )
}

CustomRoleForm.propTypes = {}
export default CustomRoleForm
